import { useState, type FormEvent } from "react";
import { Modal } from "../components/Modal";
import {
  Alert,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  Mono,
  PageHeader,
  Select,
  Table,
  TableSkeleton,
  Td,
  Th,
  Tag,
} from "../components/ui";
import { HOURS_PER_MONTH, money, rate } from "../lib/money";
import { useAddons, useUpdateAddon } from "../lib/queries";
import type { Addon } from "../lib/types";

/**
 * Add-on pricing.
 *
 * An add-on is a priced thing that is not a resource tier — a scheduled job has
 * no CPU or memory to sell, so it cannot be a plan. Editing one changes what
 * every tenant holding it pays from the next hourly run, which is why this is a
 * superadmin write and why a rise is called out rather than quietly applied.
 */
export function AddonsPage() {
  const addons = useAddons();
  const update = useUpdateAddon();

  const [editing, setEditing] = useState<Addon | null>(null);
  const [priceCents, setPriceCents] = useState(0);
  const [state, setState] = useState<"active" | "archived">("active");
  const [notice, setNotice] = useState<string | null>(null);

  const rows = addons.data?.data ?? [];

  function open(addon: Addon) {
    setEditing(addon);
    setPriceCents(addon.priceCents);
    setState(addon.state);
    setNotice(null);
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!editing) return;
    const result = await update.mutateAsync({ id: editing.id, price_cents: priceCents, state });
    // The API decides whether this was a rise; the console reports what it
    // said rather than working it out a second time and possibly differently.
    setNotice(result.notice ?? null);
    if (!result.notice) setEditing(null);
  }

  const raising = editing ? priceCents > editing.priceCents : false;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Add-ons"
        description="Priced things that are not a resource tier. Editing one changes what every tenant holding it pays."
      />

      {addons.error && <Alert>{addons.error.message}</Alert>}
      {update.error && <Alert>{update.error.message}</Alert>}

      <Card>
        {addons.isPending ? (
          <TableSkeleton columns={7} />
        ) : rows.length === 0 ? (
          <EmptyState
            title="No add-ons"
            description="Migration 0098 seeds the schedule add-on. If this is empty, the schema is ahead of its data."
          />
        ) : (
          <Table
            head={
              <>
                <Th>Add-on</Th>
                <Th>Slug</Th>
                <Th>Monthly</Th>
                <Th>Hourly</Th>
                <Th>In use</Th>
                <Th>State</Th>
                <Th />
              </>
            }
          >
            {rows.map((addon) => (
              <tr key={addon.id}>
                <Td>
                  <div className="text-fg">{addon.name}</div>
                  {addon.description && (
                    <div className="text-[11px] text-fg-subtle">{addon.description}</div>
                  )}
                </Td>
                <Td>
                  <Mono className="text-[12px]">{addon.slug}</Mono>
                </Td>
                <Td className="text-fg">{money(addon.priceCents * 10_000, addon.currency)}</Td>
                <Td className="text-fg-muted">
                  {/* Derived, never stored — the same floor the meter applies,
                      so this figure and the charge cannot drift. */}
                  {rate(addon.hourlyMicros, addon.currency)}
                </Td>
                <Td className="text-fg-muted">{addon.inUse}</Td>
                <Td>
                  {addon.state === "active" ? (
                    <Tag tone="good">active</Tag>
                  ) : (
                    <Tag>archived</Tag>
                  )}
                </Td>
                <Td>
                  <Button variant="secondary" size="sm" onClick={() => open(addon)}>
                    Edit
                  </Button>
                </Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      <Modal
        open={editing !== null}
        onClose={() => {
          setEditing(null);
          setNotice(null);
        }}
        title={editing ? `Edit ${editing.name}` : ""}
      >
        {editing && (
          <form onSubmit={submit} className="space-y-5">
            <Field
              label="Price, in cents per month"
              hint={`${HOURS_PER_MONTH} hours to a billing month, so this divides down to the hourly rate the meter charges.`}
            >
              <Input
                type="number"
                min={0}
                value={priceCents}
                onChange={(event) => setPriceCents(Number(event.target.value))}
              />
            </Field>

            <Field
              label="State"
              hint="Archiving stops the charge for every schedule without deleting any of them."
            >
              <Select
                value={state}
                onChange={(event) => setState(event.target.value as "active" | "archived")}
              >
                <option value="active">active</option>
                <option value="archived">archived</option>
              </Select>
            </Field>

            {/*
              Warned before the write, not only after it. §6.5 of the Terms
              commits to 30 days' notice before a price rise, and the monthly cap
              is computed from the current price — so a rise applied mid-month
              raises the ceiling for the month already running.
            */}
            {raising && (
              <Alert tone="warning">
                This is a price rise, from {money(editing.priceCents * 10_000, editing.currency)} to{" "}
                {money(priceCents * 10_000, editing.currency)} per month, affecting {editing.inUse}{" "}
                existing job{editing.inUse === 1 ? "" : "s"}. §6.5 of the Terms commits to 30 days'
                notice to affected customers before one takes effect.
              </Alert>
            )}

            {notice && <Alert tone="warning">{notice}</Alert>}

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setEditing(null);
                  setNotice(null);
                }}
              >
                {notice ? "Done" : "Cancel"}
              </Button>
              <Button type="submit" loading={update.isPending}>
                Save
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
